/**
 * Looks Hero – blog index hero (Get The Look).
 * Explore scroll + responsive alt text; adapted from section-about-hero.js.
 */

const SELECTOR_EXPLORE = '.js-aboutExplore';
const SELECTOR_RESPONSIVE_IMG = 'img[data-alt-mobile][data-alt-desktop]';
const MOBILE_BREAKPOINT = 1024;
const SCROLL_OVER_CLEAR_PX = 2;

function parseCssPx(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getNextSection(el) {
  const heroSection = el.closest('.shopify-section');
  if (!heroSection) return null;

  let sibling = heroSection.nextElementSibling;
  while (sibling) {
    if (!sibling.classList.contains('shopify-section')) {
      sibling = sibling.nextElementSibling;
      continue;
    }
    const style = getComputedStyle(sibling);
    if (style.display === 'none' || style.visibility === 'hidden') {
      sibling = sibling.nextElementSibling;
      continue;
    }
    if (sibling.getBoundingClientRect().height > 0) return sibling;
    sibling = sibling.nextElementSibling;
  }
  return null;
}

function measureStickyTopChrome() {
  let pinnedBottom = 0;
  const seen = new Set();

  const candidates = document.querySelectorAll(
    '#shopify-section-group-header-group .shopify-section, ' +
      '#shopify-section-header, ' +
      '[id^="shopify-section-"][id*="header"], ' +
      '[id^="shopify-section-"][id*="announcement"]',
  );

  candidates.forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);

    const style = getComputedStyle(el);
    if (style.position !== 'sticky' && style.position !== 'fixed') return;

    const rect = el.getBoundingClientRect();
    if (rect.height <= 0 || rect.bottom <= 0) return;

    if (rect.top <= 1) {
      pinnedBottom = Math.max(pinnedBottom, rect.bottom);
    }
  });

  if (pinnedBottom > 0) {
    return { total: pinnedBottom };
  }

  const headerSection = document.querySelector(
    '#shopify-section-header, [id^="shopify-section-"][id$="__header"]',
  );
  const innerHeader = document.getElementById('main-header');

  const headerSectionH = headerSection ? headerSection.getBoundingClientRect().height : 0;
  const innerHeaderH = innerHeader ? innerHeader.getBoundingClientRect().height : 0;
  const headerH = Math.max(headerSectionH, innerHeaderH);

  const promoWrapper = document.querySelector('.announcement-wrapper');
  const promoSection = promoWrapper?.closest('.shopify-section');
  const promoStyle = promoSection ? getComputedStyle(promoSection) : null;
  const promoPinned =
    promoSection &&
    promoStyle &&
    (promoStyle.position === 'sticky' || promoStyle.position === 'fixed') &&
    promoSection.getBoundingClientRect().top <= 1
      ? promoSection.getBoundingClientRect().bottom
      : 0;

  const cssFallback =
    parseCssPx(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
    70;

  return {
    total: Math.max(headerH + promoPinned, cssFallback),
  };
}

class LooksHero extends HTMLElement {
  constructor() {
    super();
    this._onExploreClick = this._onExploreClick.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._resizeRaf = null;
    this._scrollRaf = null;
  }

  connectedCallback() {
    this.classList.add('is-in-view');
    this._bindEvents();
    this._updateResponsiveAltText();
    this._bindScrollState();
    this._updateScrollState();
  }

  disconnectedCallback() {
    this._unbindEvents();
    this._unbindScrollState();
  }

  _onExploreClick() {
    const nextSection = getNextSection(this);
    if (!nextSection) return;

    const scrollY = window.scrollY;
    const nextRect = nextSection.getBoundingClientRect();
    const stickyOffset = measureStickyTopChrome().total;
    const nextTopDoc = nextRect.top + scrollY;
    const targetY = nextTopDoc - stickyOffset + SCROLL_OVER_CLEAR_PX;

    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  _updateResponsiveAltText() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    this.querySelectorAll(SELECTOR_RESPONSIVE_IMG).forEach((img) => {
      const alt = isMobile ? img.dataset.altMobile : img.dataset.altDesktop;
      if (typeof alt === 'string') img.setAttribute('alt', alt);
    });
  }

  _onResize() {
    if (this._resizeRaf) return;
    this._resizeRaf = window.requestAnimationFrame(() => {
      this._resizeRaf = null;
      this._updateResponsiveAltText();
    });
  }

  _updateScrollState() {
    const elBottom = this.offsetTop + this.offsetHeight;
    const viewBottom = window.scrollY + window.innerHeight;
    this.classList.toggle('is-past-bottom', viewBottom > elBottom);
  }

  _onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = window.requestAnimationFrame(() => {
      this._scrollRaf = null;
      this._updateScrollState();
    });
  }

  _bindScrollState() {
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  _unbindScrollState() {
    window.removeEventListener('scroll', this._onScroll);
    if (this._scrollRaf) {
      window.cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
  }

  _bindEvents() {
    this.querySelectorAll(SELECTOR_EXPLORE).forEach((el) => {
      el.addEventListener('click', this._onExploreClick);
    });
    window.addEventListener('resize', this._onResize);
  }

  _unbindEvents() {
    this.querySelectorAll(SELECTOR_EXPLORE).forEach((el) => {
      el.removeEventListener('click', this._onExploreClick);
    });
    window.removeEventListener('resize', this._onResize);
    if (this._resizeRaf) {
      window.cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = null;
    }
  }
}

if (!customElements.get('looks-hero')) {
  customElements.define('looks-hero', LooksHero);
}
